import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthentication } from "@commons/Hooks";
import { ConfiguracionInicialContext } from "../Contexts/ConfiguracionInicialContext";
import { ejecutarCierre } from "../Utils/cierreConfiguracionInicial";
import {
  calcularProgreso,
  crearClaveRespuesta,
  crearIndiceRelacionados,
  ESTADOS_RESPUESTA,
  normalizarCarga,
  obtenerPrimero,
} from "../Utils/configuracionInicial.constants";
import {
  aplicarYCompletarConfiguracionInicial,
  completarConfiguracionInicial,
  consultarEstadoConfiguracionInicial,
  generarPlanConfiguracionInicial,
  guardarRespuestaConfiguracionInicial,
  iniciarConfiguracionInicial,
  omitirConfiguracionInicial,
  obtenerConfiguracionInicial,
  posponerConfiguracionInicialEnSesion,
} from "../Services/configuracionInicialService";

const versionProcesoDe = (proceso) =>
  obtenerPrimero(proceso, ["VersionRegistroProceso", "versionRegistroProceso", "VersionRegistro", "versionRegistro"], null);

const versionRespuestaDe = (respuesta) =>
  obtenerPrimero(respuesta, ["VersionRegistroRespuesta", "versionRegistroRespuesta", "VersionRegistro", "versionRegistro"], null);

const versionPlanDe = (plan) =>
  obtenerPrimero(plan, ["VersionRegistroPlan", "versionRegistroPlan", "VersionRegistro", "versionRegistro"], null);

export const ConfiguracionInicialProvider = ({ children, esRutaWizard = false }) => {
  const { usuario } = useAuthentication();
  const [estadoAcceso, setEstadoAcceso] = useState(null);
  const [ambitoAccesoValidado, setAmbitoAccesoValidado] = useState(null);
  const consultaAccesoActual = useRef(0);
  const [proceso, setProceso] = useState(null);
  const [definicion, setDefinicion] = useState(null);
  const [respuestas, setRespuestas] = useState([]);
  const [plan, setPlan] = useState(null);
  const [valoresActuales, setValoresActuales] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [diferencias, setDiferencias] = useState([]);
  const [contextoIntegracionDisponible, setContextoIntegracionDisponible] = useState(false);
  const [aplicacionOperativaHabilitada, setAplicacionOperativaHabilitada] = useState(false);
  const [puedeAplicarConfiguracion, setPuedeAplicarConfiguracion] = useState(false);
  const [resultadoAplicacion, setResultadoAplicacion] = useState(null);
  const [seccionesOmitidas, setSeccionesOmitidas] = useState([]);
  const [progresoServidor, setProgresoServidor] = useState(null);
  const [bienvenidaVisible, setBienvenidaVisible] = useState(false);
  const [consultandoEstado, setConsultandoEstado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const cierreEnCurso = useRef(false);
  const [faseCierre, setFaseCierre] = useState(null);

  const aplicarCarga = useCallback((datos) => {
    const carga = normalizarCarga(datos);
    setProceso(carga.proceso);
    setDefinicion(carga.definicion);
    setRespuestas(carga.respuestas);
    setPlan(carga.plan);
    setSeccionesOmitidas(carga.seccionesOmitidas);
    setProgresoServidor(carga.progresoServidor);
    setValoresActuales(carga.contextoIntegracionDisponible ? carga.valoresActuales : []);
    setSugerencias(carga.contextoIntegracionDisponible ? carga.sugerencias : []);
    setDiferencias(carga.contextoIntegracionDisponible ? carga.diferencias : []);
    setContextoIntegracionDisponible(carga.contextoIntegracionDisponible);
    setAplicacionOperativaHabilitada(carga.aplicacionOperativaHabilitada);
    setPuedeAplicarConfiguracion(carga.puedeAplicarConfiguracion);
    return carga;
  }, []);

  const consultarEstado = useCallback(async () => {
    if (!usuario) return null;
    const consultaId = ++consultaAccesoActual.current;
    setConsultandoEstado(true);
    setError(null);
    try {
      const datos = await consultarEstadoConfiguracionInicial();
      if (consultaId !== consultaAccesoActual.current) return null;
      setEstadoAcceso(datos || null);
      setAmbitoAccesoValidado({ usuario, esRutaWizard });
      const mostrarBienvenida = obtenerPrimero(
        datos,
        ["MostrarBienvenidaInicial", "mostrarBienvenidaInicial", "MostrarAsistente", "mostrarAsistente"],
        false
      ) === true;
      // La bienvenida presenta el recorrido una sola vez, antes de iniciarlo.
      // Un proceso ya comenzado se retoma desde el recordatorio del ERP.
      setBienvenidaVisible(mostrarBienvenida);
      return datos;
    } catch (excepcion) {
      if (consultaId !== consultaAccesoActual.current) return null;
      setEstadoAcceso(null);
      setAmbitoAccesoValidado({ usuario, esRutaWizard });
      setBienvenidaVisible(false);
      setError(excepcion);
      return null;
    } finally {
      if (consultaId === consultaAccesoActual.current) setConsultandoEstado(false);
    }
  }, [usuario, esRutaWizard]);

  useEffect(() => {
    if (usuario === undefined) return;
    if (usuario === null) {
      consultaAccesoActual.current += 1;
      setEstadoAcceso(null);
      setAmbitoAccesoValidado(null);
      setConsultandoEstado(false);
      setBienvenidaVisible(false);
      setProceso(null);
      setDefinicion(null);
      setRespuestas([]);
      setPlan(null);
      setValoresActuales([]);
      setSugerencias([]);
      setDiferencias([]);
      setContextoIntegracionDisponible(false);
      setAplicacionOperativaHabilitada(false);
      setPuedeAplicarConfiguracion(false);
      setResultadoAplicacion(null);
      setSeccionesOmitidas([]);
      return;
    }
    consultarEstado();
    return () => { consultaAccesoActual.current += 1; };
  }, [usuario, consultarEstado]);

  const recargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      return aplicarCarga(await obtenerConfiguracionInicial());
    } catch (excepcion) {
      setError(excepcion);
      throw excepcion;
    } finally {
      setCargando(false);
    }
  }, [aplicarCarga]);

  const iniciarYCargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    setValoresActuales([]);
    setSugerencias([]);
    setDiferencias([]);
    setContextoIntegracionDisponible(false);
    setAplicacionOperativaHabilitada(false);
    setPuedeAplicarConfiguracion(false);
    setResultadoAplicacion(null);
    try {
      const carga = aplicarCarga(await iniciarConfiguracionInicial());
      setBienvenidaVisible(false);
      return carga;
    } catch (excepcion) {
      setError(excepcion);
      throw excepcion;
    } finally {
      setCargando(false);
    }
  }, [aplicarCarga]);

  const indiceRespuestas = useMemo(() => {
    const indice = new Map();
    respuestas.forEach((respuesta) => {
      const codigo = obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento"]);
      const ocurrencia = obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"], "UNICA");
      indice.set(crearClaveRespuesta(codigo, ocurrencia), respuesta);
    });
    return indice;
  }, [respuestas]);

  const indiceValoresActuales = useMemo(
    () => crearIndiceRelacionados(valoresActuales),
    [valoresActuales]
  );
  const indiceSugerencias = useMemo(
    () => crearIndiceRelacionados(sugerencias),
    [sugerencias]
  );
  const indiceDiferencias = useMemo(
    () => crearIndiceRelacionados(diferencias),
    [diferencias]
  );

  const guardar = useCallback(async ({
    codigoElemento,
    codigoOcurrencia = "UNICA",
    codigoOcurrenciaPadre = null,
    estadoRespuesta = ESTADOS_RESPUESTA.RESPONDIDA,
    contenidoRespuestaJson = null,
    observacion = null,
    codigoEtapaActual = null,
    codigoElementoActual = null,
    codigoOcurrenciaActual = "UNICA",
    soloActualizarUbicacion = false,
  }) => {
    const respuestasAnteriores = respuestas;
    const progresoServidorAnterior = progresoServidor;
    const claveRespuesta = crearClaveRespuesta(codigoElemento, codigoOcurrencia);
    const respuestaAnterior = indiceRespuestas.get(claveRespuesta);

    if (!soloActualizarUbicacion && codigoElemento) {
      const respuestaOptimista = {
        ...(respuestaAnterior || {}),
        CodigoElemento: codigoElemento,
        CodigoOcurrencia: codigoOcurrencia,
        CodigoOcurrenciaPadre: codigoOcurrenciaPadre,
        EstadoRespuesta: estadoRespuesta,
        ContenidoRespuestaJson: contenidoRespuestaJson,
        Observacion: observacion,
        VersionRegistroRespuesta: versionRespuestaDe(respuestaAnterior),
        Aplicable: obtenerPrimero(respuestaAnterior, ["Aplicable", "aplicable"], true),
        FechaCreacion: obtenerPrimero(
          respuestaAnterior,
          ["FechaCreacion", "fechaCreacion"],
          new Date().toISOString()
        ),
        EsOptimista: true,
      };
      // Reflejar la decisión en la pregunta, el recorrido y el progreso antes
      // de esperar la respuesta del servidor. La carga autoritativa reemplaza
      // este valor al finalizar; ante un error se restaura el estado anterior.
      setProgresoServidor(null);
      setRespuestas((actuales) => {
        const indice = actuales.findIndex((item) => crearClaveRespuesta(
          obtenerPrimero(item, ["CodigoElemento", "codigoElemento"]),
          obtenerPrimero(item, ["CodigoOcurrencia", "codigoOcurrencia"], "UNICA")
        ) === claveRespuesta);
        if (indice < 0) return [...actuales, respuestaOptimista];
        return actuales.map((item, posicion) => posicion === indice ? respuestaOptimista : item);
      });
    }

    setGuardando(true);
    setError(null);
    try {
      const carga = aplicarCarga(await guardarRespuestaConfiguracionInicial({
        CodigoElemento: codigoElemento,
        CodigoOcurrencia: codigoOcurrencia,
        CodigoOcurrenciaPadre: codigoOcurrenciaPadre,
        EstadoRespuesta: estadoRespuesta,
        ContenidoRespuestaJson: contenidoRespuestaJson,
        Observacion: observacion,
        VersionRegistroProceso: versionProcesoDe(proceso),
        VersionRegistroRespuesta: versionRespuestaDe(respuestaAnterior),
        CodigoEtapaActual: codigoEtapaActual,
        CodigoElementoActual: codigoElementoActual,
        CodigoOcurrenciaActual: codigoOcurrenciaActual,
        SoloActualizarUbicacion: soloActualizarUbicacion,
      }));
      return carga;
    } catch (excepcion) {
      if (!soloActualizarUbicacion && codigoElemento) {
        setRespuestas(respuestasAnteriores);
        setProgresoServidor(progresoServidorAnterior);
      }
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [aplicarCarga, indiceRespuestas, proceso, progresoServidor, respuestas]);

  const guardarVarias = useCallback(async (solicitudes = []) => {
    if (!Array.isArray(solicitudes) || solicitudes.length === 0) {
      return { proceso, definicion, respuestas };
    }

    let cargaActual = { proceso, definicion, respuestas };
    let ultimaRespuestaServidor = null;
    setGuardando(true);
    setError(null);
    try {
      // El servicio protege cada escritura con la versión del proceso. Las
      // respuestas se guardan en orden y cada petición utiliza la versión
      // devuelta por la anterior para evitar conflictos al confirmar el grupo.
      for (const solicitud of solicitudes) {
        const codigoOcurrencia = solicitud.codigoOcurrencia || "UNICA";
        const respuestaAnterior = (cargaActual.respuestas || []).find((respuesta) =>
          crearClaveRespuesta(
            obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento"]),
            obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"], "UNICA")
          ) === crearClaveRespuesta(solicitud.codigoElemento, codigoOcurrencia)
        );
        ultimaRespuestaServidor = await guardarRespuestaConfiguracionInicial({
          CodigoElemento: solicitud.codigoElemento,
          CodigoOcurrencia: codigoOcurrencia,
          CodigoOcurrenciaPadre: solicitud.codigoOcurrenciaPadre || null,
          EstadoRespuesta: solicitud.estadoRespuesta || ESTADOS_RESPUESTA.RESPONDIDA,
          ContenidoRespuestaJson: solicitud.contenidoRespuestaJson || null,
          Observacion: solicitud.observacion || null,
          VersionRegistroProceso: versionProcesoDe(cargaActual.proceso),
          VersionRegistroRespuesta: versionRespuestaDe(respuestaAnterior),
          CodigoEtapaActual: solicitud.codigoEtapaActual || null,
          CodigoElementoActual: solicitud.codigoElementoActual || null,
          CodigoOcurrenciaActual: solicitud.codigoOcurrenciaActual || codigoOcurrencia,
          SoloActualizarUbicacion: false,
        });
        cargaActual = normalizarCarga(ultimaRespuestaServidor);
      }
      aplicarCarga(ultimaRespuestaServidor);
      return cargaActual;
    } catch (excepcion) {
      // Si una escritura posterior falla, conservar las anteriores evita
      // repetir altas ya aceptadas y deja la versión local sincronizada.
      if (ultimaRespuestaServidor) aplicarCarga(ultimaRespuestaServidor);
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [aplicarCarga, definicion, proceso, respuestas]);

  const guardarUbicacion = useCallback(async (ubicacion, versionRegistroProceso = null) => {
    setGuardando(true);
    setError(null);
    try {
      const datos = await guardarRespuestaConfiguracionInicial({
        CodigoElemento: null,
        CodigoOcurrencia: null,
        CodigoOcurrenciaPadre: null,
        EstadoRespuesta: ESTADOS_RESPUESTA.SIN_RESPONDER,
        ContenidoRespuestaJson: null,
        Observacion: null,
        VersionRegistroProceso: versionRegistroProceso ?? versionProcesoDe(proceso),
        VersionRegistroRespuesta: null,
        CodigoEtapaActual: ubicacion.codigoEtapaActual,
        CodigoElementoActual: ubicacion.codigoElementoActual,
        CodigoOcurrenciaActual: ubicacion.codigoOcurrenciaActual || "UNICA",
        SoloActualizarUbicacion: true,
      });
      const carga = normalizarCarga(datos);
      // Guardar la ubicación no debe reemplazar respuestas/definición: hacerlo
      // borraría borradores locales mientras el usuario cambia el foco.
      setProceso(carga.proceso);
      return carga;
    } catch (excepcion) {
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [proceso]);

  const posponer = useCallback(async () => {
    const consultaId = consultaAccesoActual.current;
    setGuardando(true);
    setError(null);
    try {
      const datos = await posponerConfiguracionInicialEnSesion();
      if (consultaId !== consultaAccesoActual.current) return null;
      setEstadoAcceso(datos || null);
      setBienvenidaVisible(false);
      return datos;
    } catch (excepcion) {
      if (consultaId !== consultaAccesoActual.current) return null;
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, []);

  const generarPlan = useCallback(async () => {
    setGuardando(true);
    setError(null);
    try {
      await generarPlanConfiguracionInicial({ VersionRegistroProceso: versionProcesoDe(proceso) });
      return await recargar();
    } catch (excepcion) {
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [proceso, recargar]);

  const completar = useCallback(async (convertirParaDespuesEnOmitidas) => {
    setGuardando(true);
    setError(null);
    try {
      const carga = aplicarCarga(await completarConfiguracionInicial({
        VersionRegistroProceso: versionProcesoDe(proceso),
        ConvertirParaDespuesEnOmitidas: convertirParaDespuesEnOmitidas === true,
      }));
      await consultarEstado();
      return carga;
    } catch (excepcion) {
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [aplicarCarga, consultarEstado, proceso]);

  const aplicarYCompletar = useCallback(async () => {
    const versionProceso = versionProcesoDe(proceso);
    const versionPlan = versionPlanDe(plan);
    if (versionProceso === null || versionProceso === undefined) {
      throw new Error("No se encontró la versión vigente del proceso.");
    }
    if (versionPlan === null || versionPlan === undefined) {
      throw new Error("No se encontró la versión vigente del plan.");
    }
    setGuardando(true);
    setError(null);
    try {
      const resultado = await aplicarYCompletarConfiguracionInicial({
        VersionRegistroProceso: versionProceso,
        VersionRegistroPlan: versionPlan,
        ConfirmacionExplicita: true,
      });
      setResultadoAplicacion(resultado || { Exito: true });
      const carga = normalizarCarga(resultado);
      if (carga.proceso || carga.definicion) aplicarCarga(resultado);
      await consultarEstado();
      return resultado;
    } catch (excepcion) {
      setError(excepcion);
      try {
        // El backend conserva por CAS el resultado seguro del intento fallido.
        // Refrescar aquí evita que un segundo intento reutilice una versión de
        // plan obsoleta; la excepción original sigue siendo la que ve el usuario.
        aplicarCarga(await obtenerConfiguracionInicial());
      } catch (_) {
        // Un fallo de refresco no debe ocultar el error operativo original.
      }
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [aplicarCarga, consultarEstado, plan, proceso]);

  const finalizarAsistente = useCallback(async () => {
    if (cierreEnCurso.current || guardando) return;
    cierreEnCurso.current = true;
    setGuardando(true);
    setError(null);
    setFaseCierre('Comprobando tus respuestas…');
    try {
      const resultado = await ejecutarCierre({
        proceso,
        exigirAplicacion: contextoIntegracionDisponible,
        generar: generarPlanConfiguracionInicial,
        recargar: async () => aplicarCarga(await obtenerConfiguracionInicial()),
        aplicar: aplicarYCompletarConfiguracionInicial,
        completar: completarConfiguracionInicial,
        onAplicando: () => setFaseCierre(contextoIntegracionDisponible ? 'Aplicando tu configuración…' : 'Finalizando el asistente…'),
      });
      setResultadoAplicacion(resultado);
      const carga = normalizarCarga(resultado);
      if (carga.proceso) aplicarCarga(resultado);
      setFaseCierre('Confirmando el resultado de tu configuración…');
      await consultarEstado();
      return resultado;
    } catch (excepcion) {
      // A failed attempt may advance the plan version. Refresh without remounting the wizard.
      try { aplicarCarga(await obtenerConfiguracionInicial()); } catch (_) { /* Keep original error. */ }
      setError(excepcion);
      throw excepcion;
    } finally {
      cierreEnCurso.current = false;
      setFaseCierre(null);
      setGuardando(false);
    }
  }, [aplicarCarga, consultarEstado, contextoIntegracionDisponible, guardando, proceso]);

  const omitirTodo = useCallback(async (motivoOmision) => {
    setGuardando(true);
    try {
      const versionRegistroProceso = proceso
        ? versionProcesoDe(proceso)
        : obtenerPrimero(estadoAcceso, ["VersionRegistroProceso", "versionRegistroProceso"], null);
      if (!versionRegistroProceso) throw new Error("No se encontró la versión vigente del proceso.");
      await omitirConfiguracionInicial({
        VersionRegistroProceso: versionRegistroProceso,
        MotivoOmision: motivoOmision,
      });
      setBienvenidaVisible(false);
      await consultarEstado();
    } catch (excepcion) {
      setError(excepcion);
      throw excepcion;
    } finally {
      setGuardando(false);
    }
  }, [consultarEstado, estadoAcceso, proceso]);

  const progresoCalculado = useMemo(
    () => progresoServidor || calcularProgreso(definicion, respuestas),
    [definicion, progresoServidor, respuestas]
  );

  // El cambio de contraseña sucede en el iframe: su redirección no remonta
  // este provider ni el usuario de React. Al entrar al wizard, no reutilizar
  // el rechazo anterior (ni un permiso anterior) mientras se consulta al servidor.
  const accesoVigente = !!usuario &&
    ambitoAccesoValidado?.usuario === usuario &&
    ambitoAccesoValidado?.esRutaWizard === esRutaWizard;
  const accesoPendiente = !!usuario && !accesoVigente;
  const puedeAcceder = accesoVigente && obtenerPrimero(
    estadoAcceso,
    ["PuedeContinuar", "puedeContinuar", "PuedeAcceder", "puedeAcceder"],
    false
  ) === true;
  const disponible = accesoVigente && obtenerPrimero(
    estadoAcceso,
    ["Disponible", "disponible"],
    puedeAcceder || obtenerPrimero(estadoAcceso, ["Existe", "existe", "MostrarAutomaticamente", "mostrarAutomaticamente"], false) === true
  ) === true;

  const valor = useMemo(() => ({
    estadoAcceso: accesoVigente ? estadoAcceso : null,
    proceso,
    definicion,
    respuestas,
    indiceRespuestas,
    plan,
    valoresActuales,
    sugerencias,
    diferencias,
    indiceValoresActuales,
    indiceSugerencias,
    indiceDiferencias,
    contextoIntegracionDisponible,
    aplicacionOperativaHabilitada,
    puedeAplicarConfiguracion,
    resultadoAplicacion,
    seccionesOmitidas,
    progreso: progresoCalculado,
    bienvenidaVisible: accesoVigente && bienvenidaVisible,
    consultandoEstado: accesoPendiente || consultandoEstado,
    cargando,
    guardando,
    error,
    disponible,
    puedeAcceder,
    consultarEstado,
    iniciarYCargar,
    recargar,
    guardarRespuesta: guardar,
    guardarRespuestas: guardarVarias,
    guardarUbicacion,
    posponer,
    omitirTodo,
    generarPlan,
    completar,
    aplicarYCompletar,
    finalizarAsistente,
    faseCierre,
    cerrarBienvenida: () => setBienvenidaVisible(false),
    limpiarError: () => setError(null),
  }), [
    accesoPendiente, accesoVigente, aplicarYCompletar, finalizarAsistente, faseCierre, bienvenidaVisible, cargando, completar, consultandoEstado, consultarEstado,
    contextoIntegracionDisponible, aplicacionOperativaHabilitada, definicion, diferencias, disponible, error, estadoAcceso,
    generarPlan, guardando, guardar, guardarUbicacion, guardarVarias, indiceDiferencias, indiceRespuestas,
    indiceSugerencias, indiceValoresActuales, iniciarYCargar, omitirTodo, plan, posponer,
    proceso, progresoCalculado, puedeAcceder, puedeAplicarConfiguracion, recargar, respuestas,
    resultadoAplicacion, seccionesOmitidas, sugerencias, valoresActuales,
  ]);

  return (
    <ConfiguracionInicialContext.Provider value={valor}>
      {children}
    </ConfiguracionInicialContext.Provider>
  );
};
